import { useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { RocketDealsForm } from '@/components/RocketSearch/RocketDealsForm'
import { RocketSearchTopbar } from '@/components/RocketSearch/RocketSearchTopbar'
import { RocketSearchFilters } from '@/components/RocketSearch/RocketSearchFilters'
import { RocketMetricsPanel } from '@/components/RocketSearch/RocketMetricsPanel'
import { buildRocketTabs } from '@/components/RocketSearch/rocketSearchTabs'
import { RocketSearchPipelinePanel } from '@/components/RocketSearch/RocketSearchPipelinePanel'
import { RocketSearchHistoryPanel } from '@/components/RocketSearch/RocketSearchHistoryPanel'
import { RocketSearchTrashPanel } from '@/components/RocketSearch/RocketSearchTrashPanel'
import { RocketSearchDealDetailModal } from '@/components/RocketSearch/RocketSearchDealDetailModal'
import { useAuthStore } from '@/stores/authStore'
import { useRocketSearch } from '@/hooks/useRocketSearch'

export default function RocketSearch() {
  const { user } = useAuthStore()
  const isAdmin = user?.role?.toLowerCase() === 'admin'

  const rocket = useRocketSearch(isAdmin)

  const rocketTabs = useMemo(
    () => buildRocketTabs(rocket.rocketDeals.length, rocket.closedDeals.length, rocket.deletedDeals.length),
    [rocket.rocketDeals.length, rocket.closedDeals.length, rocket.deletedDeals.length],
  )

  return (
    <DashboardLayout title="Rocket Search">
      <AnimatePresence>
        {rocket.activeTab === 'pipeline' && rocket.metricsVisible && (
          <motion.section
            initial={{ height: 0, opacity: 0, marginBottom: 0 }}
            animate={{ height: 'auto', opacity: 1, marginBottom: 20 }}
            exit={{ height: 0, opacity: 0, marginBottom: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <RocketMetricsPanel deals={rocket.pipelineDeals} closerFilter={rocket.closerFilter} />
          </motion.section>
        )}
      </AnimatePresence>

      <RocketSearchTopbar
        tabs={rocketTabs}
        activeTab={rocket.activeTab}
        onTabChange={rocket.setActiveTab}
        viewMode={rocket.viewModeDeals}
        onViewModeChange={rocket.setViewModeDeals}
        metricsVisible={rocket.metricsVisible}
        onMetricsVisibleChange={rocket.setMetricsVisible}
        filters={
          rocket.activeTab === 'pipeline' ? (
            <RocketSearchFilters
              searchTerm={rocket.searchTerm}
              onSearchChange={rocket.setSearchTerm}
              closerFilter={rocket.closerFilter}
              onCloserFilterChange={rocket.setCloserFilter}
              etapaFilter={rocket.etapaFilter}
              onEtapaFilterChange={rocket.setEtapaFilter}
              stages={rocket.pipelineStages}
              closers={rocket.closers}
              isAdmin={isAdmin}
              onNewDeal={() => rocket.setIsCreateModalOpen(true)}
            />
          ) : undefined
        }
      />

      {rocket.activeTab === 'pipeline' && (
        <RocketSearchPipelinePanel
          deals={rocket.pipelineDeals}
          loading={rocket.loadingDeals}
          viewMode={rocket.viewModeDeals}
          onMoveStage={rocket.handleMoveStage}
          onDelete={rocket.handleSoftDelete}
          onDealClick={rocket.setSelectedDeal}
        />
      )}

      {rocket.activeTab === 'history' && (
        <RocketSearchHistoryPanel deals={rocket.closedDeals} onReopen={(id) => rocket.handleMoveStage(id, 'qualificado')} />
      )}

      {rocket.activeTab === 'trash' && (
        <RocketSearchTrashPanel deals={rocket.deletedDeals} onRestore={rocket.handleRestore} />
      )}

      <RocketDealsForm
        open={rocket.isCreateModalOpen}
        onClose={() => rocket.setIsCreateModalOpen(false)}
        onSuccess={rocket.loadRocketDeals}
      />

      <RocketSearchDealDetailModal
        deal={rocket.selectedDeal}
        onClose={() => rocket.setSelectedDeal(null)}
        onDelete={rocket.handleSoftDelete}
      />
    </DashboardLayout>
  )
}
